import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
    Checkbox,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    styled,
    Typography,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import type { SxProps } from '@mui/system';
import { Stack } from '@mui/system';
import Image from 'next/image';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FileExtensionConstants } from '@/src/shared/constants/CommonConstants';
import type { ConversationSummary } from '@/src/shared/types';
import { primaryOrange } from '@/src/styles/theme/lightThemeOptions';
import { getBlobUrl } from '@/src/utils/commonUtil';
import { getRelativeTimeGroupKey } from '@/src/utils/dateHelper';

import { EllipsisTypography } from '../../EllipsisTypography';

const TypographyStyle: SxProps = {
    fontWeight: '400',
    fontSize: '12px',
    lineHeight: '15.67px',
};
const StyledList = styled(List)<{ isSelectionMode: boolean }>(({ isSelectionMode }) => ({
    '& .MuiListItem-root': {
        height: '32px',
        padding: '0',
        '&:hover .delete-icon': {
            display: 'inline-flex',
            '&:hover': {
                color: primaryOrange,
            },
        },
        '&:hover .ellipsis-typography': {
            marginRight: isSelectionMode ? '' : '40px',
        },
    },
    '& .time-group-title': {
        fontWeight: '600',
        fontSize: '12px',
        lineHeight: '15.67px',
    },
    '& .time-group-title:not(:first-child)': {
        marginTop: '12px',
    },
    '& .conversation-list-item': {
        '& .MuiListItemSecondaryAction-root': {
            right: 0,
        },
        '& .MuiListItemButton-root': {
            padding: '8px 0',
        },
        '& .MuiTypography-root': TypographyStyle,
    },
    '& .MuiListItemIcon-root': {
        minWidth: 'unset',
        marginRight: '12px',
    },
    '& .load-more-button': {
        justifyContent: 'center',
        color: primaryOrange,
        '& .MuiTypography-root': {
            cursor: 'pointer',
            textDecoration: 'underline',
        },
    },
    '& .delete-icon': {
        display: 'none',
    },
}));

const NoChatStack = styled(Stack)({
    marginTop: '100px',
    gap: '12px',
    '& .MuiTypography-root': TypographyStyle,
});

export const ConversationSummaryList = ({
    isSelectionMode,
    isLoading,
    isLastPage,
    selectedSummaryIds,
    setSelectedSummaryIds,
    currentConversationId,
    conversationSummaries,
    switchCurrentConversation,
    onLoadMore,
    onDelete,
}: {
    isSelectionMode: boolean;
    isLoading: boolean;
    isLastPage: boolean;
    selectedSummaryIds: string[];
    setSelectedSummaryIds: Dispatch<SetStateAction<string[]>>;
    currentConversationId: string;
    conversationSummaries: ConversationSummary[];
    switchCurrentConversation: (conversationId: string) => void;
    onLoadMore: () => void;
    onDelete: (conversationId: string) => void;
}) => {
    const { t } = useTranslation('common');
    const isSelectAllChecked = useMemo(
        () => selectedSummaryIds.length === conversationSummaries.length,
        [selectedSummaryIds, conversationSummaries]
    );
    const toggleSelectAll = useCallback(() => {
        setSelectedSummaryIds(
            isSelectAllChecked
                ? []
                : conversationSummaries.map(({ conversationId }) => conversationId)
        );
    }, [isSelectAllChecked, conversationSummaries]);

    const selectSummaryId = useCallback(
        (conversationId: string) => {
            setSelectedSummaryIds(
                selectedSummaryIds.includes(conversationId)
                    ? selectedSummaryIds.filter((id) => id !== conversationId)
                    : [...selectedSummaryIds, conversationId]
            );
        },
        [selectedSummaryIds]
    );

    const onItemClick = useCallback(
        (conversationId: string) => {
            if (isSelectionMode) {
                selectSummaryId(conversationId);
                return;
            }
            switchCurrentConversation(conversationId);
        },
        [isSelectionMode, selectSummaryId, switchCurrentConversation]
    );

    const renderListItemToSelectAll = () => (
        <ListItem key="select-all" className="conversation-list-item">
            <ListItemButton onClick={toggleSelectAll}>
                {isSelectionMode && (
                    <ListItemIcon>
                        <Checkbox checked={isSelectAllChecked} tabIndex={-1} disableRipple />
                    </ListItemIcon>
                )}
                <Typography>{t('action.select_all')}</Typography>
            </ListItemButton>
        </ListItem>
    );

    const renderListItems = () => {
        let lastTimeGroupKey = '';
        return conversationSummaries.map(({ conversationId, lastQuery, lastQueryTimestamp }) => {
            const isSelected = conversationId === currentConversationId;
            const currentTimeGroupKey = getRelativeTimeGroupKey(lastQueryTimestamp);
            const shouldAddTimeGroupTitle = lastTimeGroupKey !== currentTimeGroupKey;
            lastTimeGroupKey = currentTimeGroupKey;

            return (
                <>
                    {shouldAddTimeGroupTitle && (
                        <ListItem key={currentTimeGroupKey} className="time-group-title">
                            {t(currentTimeGroupKey)}
                        </ListItem>
                    )}
                    <ListItem
                        key={conversationId}
                        className="conversation-list-item"
                        secondaryAction={
                            !isSelectionMode &&
                            !isLoading && (
                                <IconButton
                                    disableRipple
                                    className="delete-icon"
                                    onClick={() => onDelete(conversationId)}
                                >
                                    <DeleteOutlineIcon />
                                </IconButton>
                            )
                        }
                    >
                        <ListItemButton
                            selected={isSelected}
                            disabled={isLoading}
                            onClick={() => onItemClick(conversationId)}
                        >
                            {isSelectionMode && (
                                <ListItemIcon>
                                    <Checkbox
                                        checked={selectedSummaryIds.includes(conversationId)}
                                        tabIndex={-1}
                                        disableRipple
                                    />
                                </ListItemIcon>
                            )}
                            <EllipsisTypography>{lastQuery}</EllipsisTypography>
                        </ListItemButton>
                    </ListItem>
                </>
            );
        });
    };

    return (
        <>
            {!conversationSummaries.length && isLastPage && (
                <NoChatStack alignItems="center">
                    <Image
                        src={getBlobUrl('no-chat-mask', FileExtensionConstants.png)}
                        alt=""
                        width={300}
                        height={152}
                    />
                    <Typography>{t('ginger.tip.no_chats_available', { ns: 'ginger' })}</Typography>
                </NoChatStack>
            )}
            <StyledList className="conversation-list" isSelectionMode={isSelectionMode}>
                {isSelectionMode && !!conversationSummaries.length && renderListItemToSelectAll()}
                {renderListItems()}
                {!isSelectionMode && !isLastPage && (
                    <ListItem
                        key="load-more-button"
                        className="conversation-list-item load-more-button"
                    >
                        {isLoading ? (
                            <CircularProgress size={16} />
                        ) : (
                            <Typography onClick={isLoading ? undefined : onLoadMore}>
                                {t('action.load_more')}
                            </Typography>
                        )}
                    </ListItem>
                )}
            </StyledList>
        </>
    );
};
